<!--
@component
Navbar.Root

A responsive navigation bar root component that supports dynamic styling,
menu toggling, and search functionality.

@prop colorVariant - The color variant of the navbar.
@prop css - Custom CSS styles to be applied to the navbar.

@example
<Navbar.Root colorVariant="primary">
  ...components
</Navbar.Root>
-->

<script lang="ts">
  import type { NavbarRootProps } from '../navbar.types';
  import { setContext } from 'svelte';
  import { css } from '@agapi/ui/panda/css';
  import { backdropStyles, navbarStyles, navbarWrapperStyles } from '../navbar.styles';
  import { dynamicColorStyles } from '@agapi/ui/panda/utils';
  import { createNavbarStore } from '../navbar-store.svelte';
  import { fade } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';

  /** Props for the Navbar.Root component. */
  let { id, children, css: cssProp = {}, colorVariant = "default", menu, drawer, underHeader, underHeaderStyles }: NavbarRootProps = $props();

  /** Reference to the navbar element. */
  let navbarRef: HTMLElement;

  /** Reference to the backdrop element. */
  let backdropRef: HTMLElement | undefined = $state();

  /** Store for managing navbar state. */
  const navbarStore = createNavbarStore();

  // Set the navbar store in the context
  setContext('navbar', navbarStore);

  /** Classes for the navbar, dependent on menu open state. */
  const classes = navbarStyles.raw({menuOpen: navbarStore.isMenuOpen});

  /** Class for the navbar wrapper, derived from menu open state. */
  const navbarWrapperClass = $derived(navbarWrapperStyles({menuOpen: navbarStore.isMenuOpen}));

  let underHeaderHeight = $state(0);
  let height = $state(0);

  /**
   * Effect to update the navbar bottom position.
   * This sets a CSS custom property for use elsewhere in the app.
   */
  $effect(() => document.documentElement.style.setProperty('--navbarHeight', `${height - underHeaderHeight}px`));
  $effect(() => document.documentElement.style.setProperty('--underHeaderHeight', `${underHeaderHeight}px`));
  $effect(() => document.documentElement.style.setProperty('--navbarBottom', `${height}px`));

  /** Open the cart menu. */
  export function openDrawer() {
    navbarStore.openDrawer();
  }

  /** Close the cart menu. */
  export function closeDrawer() {
    navbarStore.closeDrawer();
  }

  /** Open the menu. */
  export function openMenu() {
    navbarStore.openMenu();
  }

  /** Close the menu. */
  export function closeMenu() {
    navbarStore.closeMenu();
  }

  /** Open the search. */
  export function openSearch() {
    navbarStore.openSearch();
  }

  /** Close the search. */
  export function closeSearch() {
    navbarStore.closeSearch();
  }

  /** Close all (open menu, drawer and search)) */
  export function closeAll() {
    navbarStore.closeAll();
  }
</script>

<div class={navbarWrapperClass}>
  <header
    id={id}
    bind:this={navbarRef}
    bind:clientHeight={height}
    style="display: flex; flex-direction: column;"
  >
    <div
      class={css(classes.root, cssProp)}
      style={colorVariant ? `
        background-color: ${dynamicColorStyles(colorVariant).backgroundColor};
        color: ${dynamicColorStyles(colorVariant).color};
      ` : undefined}
    >
      {@render children?.()}
    </div>

    {#if underHeader}
      <div
        bind:clientHeight={underHeaderHeight}
        class={css(underHeaderStyles)}>
        {@render underHeader()}
      </div>
    {/if}
  </header>

  {#if navbarStore.isMenuOpen || navbarStore.isSearchOpen || navbarStore.isDrawerOpen}
    <button
      bind:this={backdropRef}
      class={backdropStyles}
      aria-label="Close menu"
      onclick={() => navbarStore.closeAll()}
      aria-hidden="false"
      transition:fade={{ duration: 430, easing: cubicOut }}
      onintrostart={() => {
        if (backdropRef && navbarStore.isDrawerFullHeight && navbarStore.isDrawerOpen) {
          backdropRef.style.zIndex = '12';
        }
      }}
      onoutroend={() => {
        if (backdropRef) {
          backdropRef.style.zIndex = 'unset';
        }
      }}
      style="z-index: {navbarStore.isDrawerFullHeight && navbarStore.isDrawerOpen ? '12' : 'unset'}"
    ></button>
  {/if}

  {@render menu?.()}
  {@render drawer?.()}
</div>
